import { all, call, takeLatest, put, select } from 'redux-saga/effects';

import {
  GET_AUTH_REQUEST,
  LOGOUT_REQUEST,
  TOKEN_REFRESH_SUCCESS,
  TOKEN_REFRESH_REQUEST,
} from 'app/dealer/login/constants';
import { SET_USER_STATE } from 'app/shared/modules/user/constants';

import { selectLeadsBenchmarkSelectedDate } from '../general/selectors';
import { leadsBenchmarkDataRequestBegin } from '../general/actions';
import { selectSelectedLeadsBenchmarkTimeDivisionFilter } from '../timeDivision/selectors';
import { setLeadsBenchmarkTimeDivision } from '../timeDivision/actions';

import {
  fullResetLeadsBenchmarkState,
  resetLeadsBenchmarkState,
  savePreviouslySelectedDate,
} from './actions';
import {
  selectPreviouslySelectedDate,
  selectPreviouslySelectedTimeDivision,
} from './selectors';

/**
 * @function savePreviouslySelectedDateSaga
 * @description
 * gets user's currently selected date from the store
 * and dispatches it to the store as a previously selected date
 */
export function* savePreviouslySelectedDateSaga() {
  const selectedDate = yield select(selectLeadsBenchmarkSelectedDate);
  const timeDivision = yield select(
    selectSelectedLeadsBenchmarkTimeDivisionFilter,
  );
  yield put(savePreviouslySelectedDate(selectedDate, timeDivision));
}

export function* triggerDataRequestAfterSessionRefresh() {
  const previouslySelectedDate = yield select(selectPreviouslySelectedDate);
  const previouslySelectedTimeDivision = yield select(
    selectPreviouslySelectedTimeDivision,
  );
  yield put(setLeadsBenchmarkTimeDivision(previouslySelectedTimeDivision));
  yield put(leadsBenchmarkDataRequestBegin({ data: previouslySelectedDate }));
}

/**
 * @function triggerResetLeadsBenchmarkState
 * @description
 * we refresh the LeadsBenchmark initial state whenever a user
 * logs in or comes back as a returning user
 *
 * we save previous data in case the user is in the middle of an active session and has
 * had his/her token refreshed
 */
export function* triggerResetLeadsBenchmarkState() {
  const selectedDate = yield select(selectLeadsBenchmarkSelectedDate);
  yield put(resetLeadsBenchmarkState(selectedDate));
}

/**
 * @function triggerFullResetLeadsBenchmarkState
 * @description
 * we do a full refresh of the LeadsBenchmark initial state whenever a user
 * logs out
 */
export function* triggerFullResetLeadsBenchmarkState() {
  yield put(fullResetLeadsBenchmarkState());
}

/**
 * @function watchTokenRefreshSuccess
 * @description
 * watches for token refresh success action and
 * triggers a data request after the token has been refreshed
 * this is important since we are resetting the statistics whenever a
 * user starts/ends an auth session
 */
export function* watchTokenRefreshSuccess() {
  yield takeLatest(
    [TOKEN_REFRESH_SUCCESS],
    triggerDataRequestAfterSessionRefresh,
  );
}

/**
 * @function watchTokenRefreshRequest
 * @description
 * watches for a token refresh request and saves
 * the currently selected date so the date picker can
 * persist it's value after the token refresh
 */
export function* watchTokenRefreshRequest() {
  yield takeLatest([TOKEN_REFRESH_REQUEST], savePreviouslySelectedDateSaga);
}

// refresh data for login and returning users
export function* watchUserAuthStatus() {
  yield takeLatest(
    [GET_AUTH_REQUEST, SET_USER_STATE],
    triggerResetLeadsBenchmarkState,
  );
}

// fully refresh data when user logs out - no previous data saved
export function* watchUserLogout() {
  yield takeLatest([LOGOUT_REQUEST], triggerFullResetLeadsBenchmarkState);
}

export function* watchUserAuthSagas() {
  yield all([
    call(watchUserAuthStatus),
    call(watchUserLogout),
    call(watchTokenRefreshRequest),
    call(watchTokenRefreshSuccess),
  ]);
}
